<template>
    <v-app>
        <div ref="header">
            <Header />
        </div>
        <v-main>
            <v-container>
                <router-view />
                <v-snackbar v-model="errors" :timeout="timeout">
                    {{ this.$store.state.errors.message }}
                    <template v-slot:action="{ attrs }">
                        <v-btn :color="localSnackColor" text v-bind="attrs" @click="$store.commit('closeErrors')">Close</v-btn>
                    </template>
                </v-snackbar>
            </v-container>
        </v-main>
        <div ref="footer">
            <Footer />
        </div>
    </v-app>
</template>
<script>
import { api } from '@/utils/api';
import { mapMutations, mapState } from 'vuex';
import Header from './components/Header';
import Footer from './components/Footer';
export default {
    name: 'App',
    components: {
        Header,
        Footer
    },
    data() {
        return {
            clients: []
        };
    },
    // used to adjust the footer and header heights
    created() {
        window.addEventListener('resize', this.handleResize);
        this.handleResize();
    },
    // used to adjust the footer and header heights
    destroyed() {
        window.removeEventListener('resize', this.handleResize);
    },
    computed: {
        ...mapState({
            displayError: state => state.errors.display,
            showSnackbar: state => state.errors.showSnackbar,
            snackColor: state => state.errors.color || 'green',
            timeout: state => state.errors.timeout || -1
        }),
        errors: function () {
            return this.$store.getters.getErrorsDisplay;
        },
        localSnackColor: function () {
            return this.$store.getters.getSnackColorDisplay || 'pink';
        }
    },
    async mounted() {
        // set height's header an footer in store

        this.handleResize();

        const errno = this.getQueryVariable('errno');
        const osol = this.getQueryVariable('osol');

        this.$el.addEventListener('click', this.hideErrors);

        await api.setLang('en', this.$i18n, this.$vuetify);

        if (errno) this.$store.commit('displayErrors', { errno });
        const pathName = document?.location?.pathname;
        if (process.env.VUE_APP_ENV === 'development') {
            this.$store.commit('setUserRolesAndAuthorization', 'developmentMode');
        } else if ((pathName.startsWith('/OSOL') || pathName.includes('/OSOL/')) && osol) {
            const anonGroups = { roles: { trackandtrace: { anon: true } } };
            const userInfo = { firstname: 'osol', lastname: 'user' };

            this.$store.commit('setUserRolesAndAuthorization', anonGroups);
            this.$store.commit('setUserInformations', userInfo);
        } else {
            document.cookie = 'osolToken=;';
            const response = await api.get(['admin', 'isallowed'], null, {
                withCredentials: true
            });
            if (response?.message === 'Access Allowed') {
                const groups = await api.get(['admin', 'getconfigroles'], null, {
                    withCredentials: true
                });
                this.$store.commit('setUserRolesAndAuthorization', groups);
                const informations = await api.get(['admin', 'getname'], null, {
                    withCredentials: true
                });
                this.$store.commit('setUserInformations', informations);

                const clients = await api.get(['admin', 'clients']);
                this.clients = this.assignRolesToGroups(clients, groups);
                this.$store.commit('setClients', this.clients);

                const clientName = this.$route.params.client;

                if (clientName) {
                    const client = this.clients.find(cli => cli.name === clientName);

                    if (client) {
                        const { id, languages, defaultLang } = client;
                        const language = JSON.parse(localStorage.getItem('language') || '{}');
                        const userLang = language[id] || defaultLang || 'en';
                        if (languages) {
                            this.setLanguages(languages);
                        }
                        if (userLang) {
                            await api.setLang(userLang, this.$i18n, this.$vuetify);
                            this.selectLang(userLang);
                        }
                    }
                }
            }
        }

        const browserPaths = window.location.pathname
            .split('/')
            .map(path => (path && path !== '' ? path : null))
            .filter(path => !!path);

        let client = null;
        for (let i = 0; i < this.clients.length; i++) {
            const original_name = decodeURI(browserPaths[0]);
            if (this.clients[i].name === original_name) {
                client = this.clients[i].id;
                break;
            }
        }

        this.$store.commit('setCurrent', client);

        // fix osol path problem
        const verifyOsolPath = browserPaths[1] === 'OSOL' ? 'trackandtrace' : browserPaths[1];
        this.$store.commit('setClientService', verifyOsolPath || null);

        if (browserPaths.length === 0 && this.clients.length === 1) {
            this.$store.commit('setCurrent', this.clients[0].id);
            this.$router.push(`/${this.clients[0].name}/`);
        }
    },

    methods: {
        ...mapMutations('languages', ['setLanguages', 'selectLang']),
        ...mapMutations(['closeErrors', 'setShowSnackBar']),

        //used to resize height
        handleResize() {
            this.$store.commit('setHeightHeader', this.$refs.header?.clientHeight);
            this.$store.commit('setHeightFooter', this.$refs.footer?.clientHeight);
        },
        getQueryVariable(variable) {
            const query = window.location.search.substring(1);
            const vars = query.split('&');
            for (let i = 0; i < vars.length; i++) {
                const pair = vars[i].split('=');
                if (decodeURIComponent(pair[0]) === variable) {
                    return decodeURIComponent(pair[1]);
                }
            }
            return null;
        },
        hideErrors() {
            if (this.displayError) {
                this.closeErrors();
            }
            if (this.showSnackbar) {
                this.setShowSnackBar(false);
            }
        },

        assignRolesToGroups(data, groups) {
            const clients = JSON.parse(JSON.stringify(data));

            if (groups) {
                clients.forEach(client => {
                    client.services = client.services.reduce((acc, cur) => {
                        if (typeof cur === 'object') {
                            if (cur.url) {
                                if (groups[cur.name] && groups[cur.name].includes(client.id.toString())) {
                                    acc.push({ name: cur.name, url: cur.url });
                                }
                            } else if (groups.docsInstance?.[cur?.client_id]?.[cur?.id]) {
                                const instanceDocRole = groups.docsInstance?.[cur?.client_id]?.[cur?.id];

                                if (instanceDocRole?.writer || instanceDocRole?.viewer) {
                                    acc.push({
                                        name: cur.service_name,
                                        service: 'DOCS',
                                        requires_auth: true,
                                        id: cur.id,
                                        viewer: instanceDocRole?.viewer || false,
                                        writer: instanceDocRole?.writer || false
                                    });
                                }
                            }
                        } else if (groups[cur]) {
                            //Manage iframe services
                            if (
                                cur.includes('IFRAME') &&
                                groups[cur + 'Instance'] &&
                                groups[cur + 'Instance'][client.id] &&
                                groups[cur + 'Instance'][client.id].length !== 0
                            ) {
                                acc.push({
                                    name: cur,
                                    serviceNumber: cur.replace('IFRAME', ''),
                                    instance: groups[cur + 'Instance']
                                });
                            } else if (groups[cur].includes(client.id.toString())) {
                                acc.push({ name: cur });
                            }
                        } else if (!cur.includes('IFRAME')) {
                            acc.push({ name: cur });
                        }
                        return acc;
                    }, []);
                });
            }

            return clients
                .map(client => {
                    //clean empty iframes
                    client.services = client.services.filter(
                        service =>
                            !service.name.includes('IFRAME') ||
                            (service.name.includes('IFRAME') && service.instance && Object.keys(service.instance).length !== 0)
                    );
                    return client;
                })
                .filter(client => client.services.length);
        }
    },
    watch: {
        '$route.path'() {
            this.$store.dispatch('callRouteChanged');
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;

            if (this.$route.path === '/') {
                this.$store.commit('setCurrent', null);
            }
        }
    }
};
</script>

<style lang="scss">
@import '@/scss/global.scss';
.v-snack__wrapper {
    position: absolute !important;
    top: 50% !important;
}
</style>
